import store from '@/store';
import { Movie, Award, Subtitle, Video } from '@/store/types';
import { validate } from 'vee-validate';
import axios from 'axios';
import { i18n } from '@/setup/i18n';
import AwardUtility from '@/utilities/AwardUtility';
import { format, addDays } from 'date-fns';
import {
  language_name,
  movie_resolution_name,
  frame_rate_name,
  aspect_ratio_name,
  audio_format_name,
  film_format_name,
  rating_label,
  select_subtitle_file_format,
  select_subtitle_language,
  film_type,
  production_country,
  language,
  type,
} from '@/components/const/SelectMovieOptions';
import UserMixin from '@/Mixin/UserMixin';

export default class MovieLoader {
  key_names = [
    {
      key: 'film_name_yomi',
      validation: { required: true },
      option: {
        name: i18n.t('commons.fields.film_name_yomi'),
      },
    },
    {
      key: 'translation_status',
      validation: {},
      option: {},
    },
    {
      key: 'original_name',
      validation: { required: true },
      option: {
        name: i18n.t('commons.fields.original_name'),
      },
    },
    {
      key: 'film_type',
      validation: {
        required: true,
        oneOf: ['映画', 'テレビ番組'],
      },
      convert: film_type,
      option: {
        name: i18n.t('commons.fields.film_type'),
      },
    },
    {
      key: 'production_country',
      validation: {
        required: true,
        oneOf: ['日本', '米国', '中国'],
      },
      convert: production_country,
      option: {
        name: i18n.t('commons.fields.production_country'),
      },
    },
    {
      key: 'production_year',
      validation: { required: true },
      option: {
        name: i18n.t('commons.fields.production_year'),
      },
    },
    {
      key: 'language',
      validation: {
        required: true,
        oneOf: ['日本語', '英語'],
      },
      convert: language,
      option: {
        name: i18n.t('commons.fields.language'),
      },
    },
    {
      key: 'type',
      validation: {
        required: true,
        oneOf: ['実写', 'アニメ', 'ドキュメンタリー', 'モキュメンタリー', 'ハイブリッド'],
      },
      convert: type,
      option: {
        name: i18n.t('commons.fields.type'),
      },
    },
    {
      key: 'episode_number',
      validation: {},
      option: {},
    },
    {
      key: 'original_site',
      validation: { url: true },
      option: {},
    },
    {
      key: 'rating',
      validation: {
        oneOf: [
          'G',
          'PG12',
          'R15+',
          'R18+',
          '（未審査）G相当',
          '（未審査）PG12相当',
          '（未審査）R15+相当',
          '（未審査）R18+相当',
        ],
      },
      convert: rating_label,
      option: {
        name: i18n.t('commons.fields.rating'),
      },
    },
    {
      key: 'original_site',
      validation: {
        url: true,
      },
      option: {
        name: i18n.t('commons.fields.original_site'),
      },
    },
    {
      key: 'imdb_url',
      validation: {
        url: true,
      },
      option: {
        name: i18n.t('commons.fields.imdb_url'),
      },
    },
    {
      key: 'play_time',
      validation: {
        required: true,
      },
      option: {
        name: i18n.t('commons.fields.play_time'),
      },
    },
    {
      key: 'trailer_url',
      validation: {
        required: true,
        url: true,
      },
      option: {
        name: i18n.t('commons.fields.trailer_url'),
      },
    },
    {
      key: 'screener_url',
      validation: {
        url: true,
        required: true,
      },
      option: {
        name: i18n.t('commons.fields.screener_url'),
      },
    },
    {
      key: 'screener_password',
      validation: {},
      option: {},
    },
    // {
    //   key: 'movie_file_name',
    //   validation: {},
    //   option: {},
    // },
    {
      key: 's_o_m',
      validation: {},
      option: {},
    },
    {
      key: 'duration',
      validation: {},
      option: {},
    },
    {
      key: 'currency',
      validation: {},
      option: {},
    },
    {
      key: 'isan_number',
      validation: {},
      option: {},
    },
    {
      key: 'twitter_url',
      validation: {
        url: true,
      },
      option: {
        name: i18n.t('commons.fields.twitter_url'),
      },
    },
    {
      key: 'instagram_url',
      validation: {
        url: true,
      },
      option: {
        name: i18n.t('commons.fields.instagram_url'),
      },
    },
    {
      key: 'facebook_url',
      validation: {
        url: true,
      },
      option: {
        name: i18n.t('commons.fields.facebook_url'),
      },
    },
  ];

  translation_key_names = [
    {
      key: 'film_name',
      validation: { required: true },
      option: {},
      // option: {
      //   adminRequire: true,
      // },
    },
    {
      key: 'season_name',
      validation: {},
      option: {},
    },
    {
      key: 'episode_name',
      validation: {},
      option: {},
    },
    {
      key: 'original_from',
      validation: {},
      option: {},
    },
    {
      key: 'copyright',
      validation: {},
      option: {},
    },
    {
      key: 'copyright_holder',
      validation: {},
      option: {},
    },
    {
      key: 'creator',
      validation: {},
      option: {},
    },
    {
      key: 'director',
      validation: {
        required: true,
      },
      option: {
        name: i18n.t('commons.fields.director'),
      },
    },
    {
      key: 'script_writer',
      validation: {},
      option: {
        name: i18n.t('commons.fields.script_writer'),
      },
    },
    {
      key: 'cinematographer',
      validation: {},
      option: {
        name: i18n.t('commons.fields.cinematographer'),
      },
    },
    {
      key: 'music',
      validation: {},
      option: {
        name: i18n.t('commons.fields.music'),
      },
    },
    {
      key: 'main_cast_1',
      validation: {
        required: true,
      },
      option: {
        name: i18n.t('commons.fields.main_cast_1'),
      },
    },
    {
      key: 'main_cast_2',
      validation: {},
      option: {},
    },
    {
      key: 'main_cast_3',
      validation: {},
      option: {},
    },
    {
      key: 'main_cast_4',
      validation: {},
      option: {},
    },
    {
      key: 'main_cast_5',
      validation: {},
      option: {},
    },
    {
      key: 'outline',
      validation: {
        required: true,
      },
      option: {
        name: i18n.t('commons.fields.outline'),
      },
    },
    {
      key: 'pr_message',
      validation: {
        required: true,
      },
      option: {
        name: i18n.t('commons.fields.pr_message'),
      },
    },
  ];

  genre_keys = ['genre', 'pgenre1', 'pgenre2', 'pgenre3', 'pgenre4', 'pgenre5'];
  subgenre_keys = ['sgenre1', 'sgenre2', 'sgenre3', 'sgenre4', 'sgenre5'];
  format_keys = [
    'subtitle_languages',
    'voiceover_languages',
    'resolution',
    'frame_rate',
    'aspect_ratio',
    'audio',
    'audio_channel',
    'file_format',
  ];

  // managers_check_keys = ['original_name', 'film_type', 'format', 'type', 'play_time'];
  managers_check_translations_keys = {
    ja: ['director', 'outline', 'pr_message'],
    en: ['director'],
  };

  async localValidate(movie: Movie): Promise<object> {
    const impersonate = store.getters['users/manager/getImpersonation'];
    const isProviderImpersonate = impersonate.impersonate && impersonate.impersonateRole == 'provider';
    let errors = {};
    if (isProviderImpersonate) {
      errors = { ...errors, ...(await this.checkMovieTranslationVariables(movie, 'ja')) };
      errors = { ...errors, ...(await this.checkMovieTranslationVariables(movie, 'en')) };
    } else {
      errors = { ...errors, ...(await this.checkMovieVariables(movie)) };
      errors = { ...errors, ...(await this.checkMovieTranslationVariables(movie, 'ja')) };
      errors = { ...errors, ...(await this.checkMovieTranslationVariables(movie, 'en')) };
    }
    return Promise.resolve(errors);
  }

  async checkMovieVariables(movie: Movie): Promise<object> {
    const errors = {};
    await Promise.all(this.key_names.map(async (obj) => {}));

    //special cases
    // errors.genres = [];
    // this.genre_keys.forEach((key: string): void => {
    //   if (data[key]) {
    //     if (Object.keys(genreItem).indexOf(data[key]) === -1) {
    //       errors.genres.push(`${key}の値は選択項目の中から選んでください。`);
    //     }
    //   } else if (['genre', 'pgenre1'].indexOf(key) >= 0) {
    //     if (isProviderImpersonate && key == 'genre') {
    //       errors.genres.push(`${key}は必須項目です。`);
    //     } else if (!isProviderImpersonate) {
    //       errors.genres.push(`${key}は必須項目です。`);
    //     }
    //   }
    // });
    // if (errors.genres.length == 0) {
    //   delete errors.genres;
    // }

    return Promise.resolve(errors);
  }

  async checkMovieTranslationVariables(movie: Movie, locale: string): Promise<object> {
    const errors = {};
    await Promise.all(
      this.translation_key_names.map(async (obj) => {
        // managerの場合の必須項目チェック
        // if (isProviderImpersonate && this.managers_check_translations_keys.ja.indexOf(obj.key) < 0) {
        //   obj.validation = {};
        // }
        const result = await validate(movie.translations[locale][obj.key], obj.validation, obj.option as any);
        if (!result.valid) {
          let name = '';
          if (locale === 'ja') {
            name = obj.key;
          } else {
            name = `${obj.key}_${locale}`;
          }
          errors[name] = result.errors;
        }
      })
    );
    return Promise.resolve(errors);
  }

  async createMovieFromJSON(data: any): Promise<Movie> {
    const movie = new Movie();

    const impersonate = store.getters['users/manager/getImpersonation'];
    const isProviderImpersonate = impersonate.impersonate && impersonate.impersonateRole == 'provider';
    let impersonateProviderId = null;
    let manager = null;
    if (isProviderImpersonate) {
      manager = store.getters['users/getLoginUser'];
      impersonateProviderId = impersonate.provider.uuid;
    }

    // data copy from json to movie object.
    this.key_names.map((obj: any) => {
      if ('convert' in obj && obj.convert) {
        const found = obj.convert.find((item) => {
          return i18n.t(item.text, 'ja') === data[obj.key];
        });
        if (found !== undefined) {
          movie[obj.key] = found.value;
        }
      } else {
        movie[obj.key] = data[obj.key];
      }
    });

    this.translation_key_names.map((obj: any) => {
      if (obj.key in data) {
        if ('convert' in obj && obj.convert) {
          const found = obj.convert.find((item) => {
            return i18n.t(item.text, 'ja') === data[obj.key];
          });
          if (found !== undefined) {
            movie.translations.ja[obj.key] = found.value;
          }
        } else {
          movie.translations.ja[obj.key] = data[obj.key];
        }
      }
      if (`${obj.key}_en` in data) {
        if ('convert' in obj && (obj as any).convert) {
          const found = (obj as any).convert.find((item) => {
            return i18n.t(item.text, 'en') === data[obj.key];
          });
          if (found !== undefined) {
            movie.translations.en[obj.key] = found.value;
          }
        } else {
          movie.translations.en[obj.key] = data[`${obj.key}_en`];
        }
      }
    });

    if ('play_time' in data) {
      const len = data.play_time;
      if (len < 40) {
        movie['format'] = 1;
      } else if (len >= 40 && len < 55) {
        movie['format'] = 2;
      } else if (len >= 55) {
        movie['format'] = 3;
      }
    }

    // ratingの値変換
    if ('rating' in data && data.rating) {
      const rating = rating_label.filter((option) => {
        return data.rating == option.text;
      });
      if (rating.length > 0) {
        movie.rating = rating[0].value;
      }
    } else {
      movie.rating = 1;
    }

    // currencyのデータがない場合の自動変換。
    // if ('currency' in data || !data.currency) {
    //   movie.currency = 'USD';
    // }

    const translationErrors = {
      ja: {},
      en: {},
    };
    const translationTasks: any[] = [];
    //japanese
    this.translation_key_names.forEach((obj) => {
      if (data[obj.key] || obj.validation.required) {
        // managerの場合の必須項目チェック
        if (isProviderImpersonate && this.managers_check_translations_keys.ja.indexOf(obj.key) < 0) {
          obj.validation = {};
        }
        const translationJaTask = validate(data[obj.key], obj.validation, obj.option as any).then((result) => {
          if (result.valid) {
            (movie.translations as any).ja[obj.key] = data[obj.key] || null;
          } else {
            translationErrors.ja[obj.key] = result.errors;
          }
        });
        translationTasks.push(translationJaTask);
      }
    });

    //english
    let validateRule = {};
    this.translation_key_names.forEach((obj) => {
      if (data[`${obj.key}_en`] || obj.validation.required) {
        validateRule = { english_text: true };
        // managerの場合のバリデーション(含まれてる場合)
        if (isProviderImpersonate && this.managers_check_translations_keys.en.indexOf(obj.key) >= 0) {
          validateRule = Object.assign({ required: true }, validateRule);
        }
        if (obj.option && 'name' in obj.option) {
          obj.option.name = obj.option.name + '(英語)';
        }
        (movie.translations as any).en[obj.key] = data[`${obj.key}_en`] || null;
      }
    });

    const genreItem = {};
    await store.getters['getAllGenres'].map((item) => {
      genreItem[item.translations.ja.name] = item.uuid;
      return genreItem;
    });
    // ジャンルのフォーマット処理
    let formatGenre = {};
    let selectGenreUuid = '';
    const errors: any = {};
    errors.genres = [];
    this.genre_keys.forEach((key: string): void => {
      if (data[key]) {
        if (Object.keys(genreItem).indexOf(data[key]) >= 0) {
          selectGenreUuid = genreItem[data[key]];
          formatGenre = {
            text: data[key],
            name: data[key],
            classes: 'tag-label-class',
            is_main: true,
            uuid: selectGenreUuid,
          };
          movie.genres.push(formatGenre);
          // delete genreItem[data[key]];
        } else if (errors.genres) {
          errors.genres.push(`${key}の値は選択項目の中から選んでください。`);
        }
      } else if (['genre', 'sgenre1'].indexOf(key) >= 0) {
        if (isProviderImpersonate && key == 'genre') {
          errors.genres.push(`${key}は必須項目です。`);
        } else if (!isProviderImpersonate) {
          errors.genres.push(`${key}は必須項目です。`);
        }
      }
    });
    if (errors.genres.length == 0) {
      delete errors.genres;
    }

    // サブジャンルのフォーマット処理
    this.subgenre_keys.forEach((key: string): void => {
      if (data[key]) {
        if (Object.keys(genreItem).indexOf(data[key]) >= 0) {
          selectGenreUuid = genreItem[data[key]];
          formatGenre = {
            text: data[key],
            name: data[key],
            classes: 'tag-label-class',
            is_main: false,
            uuid: selectGenreUuid,
          };
          movie.genres.push(formatGenre);
        }
      }
    });
    // 字幕言語の設定
    // 焼き込みはmovie.main_video.videos、別ファイルはmovie.main_video.subtitles
    // Excel.column と 内容配列
    const key_subtitle_language: String = 'subtitle_language';
    const key_subtitle_language_format: String = 'subtitle_language_format';
    // 焼き込みとその他に分ける
    const tmp_subtitle = {};
    let subtitle_check: boolean = false; // subtitle_language1、subtitle_language_format1のペア必須チェック
    for (let index = 1; index < 10; index++) {
      const key_language = key_subtitle_language + index.toString();
      const key_filetype = key_subtitle_language_format + index.toString();
      if (data[key_language] && data[key_filetype]) {
        // ペア存在チェック
        subtitle_check = subtitle_check === false && index === 1 ? true : subtitle_check;
        const key =
          select_subtitle_file_format[data[key_filetype]] || select_subtitle_file_format[data[key_filetype]] === 0
            ? select_subtitle_file_format[data[key_filetype]]
            : -1;
        if (key > 0) {
          tmp_subtitle[key] = select_subtitle_language[data[key_language]];
        } else {
          if (tmp_subtitle[0] && Array.isArray(tmp_subtitle[0])) {
          } else {
            tmp_subtitle[0] = [];
          }
          tmp_subtitle[0].push(select_subtitle_language[data[key_language]]);
        }
      }
    }
    if (subtitle_check === false || Object.keys(tmp_subtitle).length == 0) {
      if (isProviderImpersonate === false) {
        // 運用がCH代理して登録した場合字幕言語がなくても登録可能
        // errors.subtitle_languages = ['選択項目の中から選択してください。'];
      }
    } else {
      // 書き込み
      Object.keys(tmp_subtitle).forEach((key) => {
        if (key === '0' && Array.isArray(tmp_subtitle[key])) {
          if (tmp_subtitle[0].length) {
            // -->> video[1]からpushする
            tmp_subtitle[0].forEach((newSubtitleLanguage) => {
              const newVideo = new Video();
              newVideo.embedded_subtitle_language = newSubtitleLanguage;
              movie.main_video.videos.push(newVideo);
            });
            // <<-- video[1]からpushする
            //--
            // -->> video[0].embedded_subtitle_languageから書いていく
            // tmp_subtitle[0].forEach((newSubtitleLanguage,idx) => {
            //   if(idx === 0){
            //     movie.main_video.videos[0].embedded_subtitle_language = newSubtitleLanguage;
            //   } else {
            //     const newVideo = new Video();
            //     newVideo.embedded_subtitle_language = newSubtitleLanguage;
            //     movie.main_video.videos.push(newVideo);
            //   }
            // });
            // <<-- video[0].embedded_subtitle_languageから書いていく
          }
        } else {
          const newSubtitle = new Subtitle();
          newSubtitle.language = tmp_subtitle[key];
          newSubtitle.file_format = parseInt(key);
          movie.main_video.subtitles.push(newSubtitle);
        }
      });
    }

    // フォーマットの設定
    this.format_keys.forEach((key: string): void => {
      if (data[key]) {
        switch (key) {
          case 'voiceover_languages':
            if (language_name[data[key]] || data[key] == 'なし') {
              movie.main_video.videos[0].embedded_voiceover_language = language_name[data[key]];
            } else {
              // errors.embedded_voiceover_language = ['選択項目の中から選択してください。'];
            }
            break;
          case 'resolution':
            if (movie_resolution_name[data[key]]) {
              movie.main_video.videos[0].resolution = movie_resolution_name[data[key]];
            } else {
              // errors.resolution = ['選択項目の中から選択してください。'];
            }
            break;
          case 'frame_rate':
            if (frame_rate_name[data[key]]) {
              movie.main_video.videos[0].frame_rate = frame_rate_name[data[key]];
            } else {
              // errors.frame_rate = ['選択項目の中から選択してください。'];
            }
            break;
          case 'aspect_ratio':
            if (aspect_ratio_name[data[key]]) {
              movie.main_video.videos[0].aspect_ratio = aspect_ratio_name[data[key]];
            } else {
              // errors.aspect_ratio = ['選択項目の中から選択してください。'];
            }
            break;
          case 'audio':
            if (audio_format_name[data[key]]) {
              movie.main_video.videos[0].audio_format = audio_format_name[data[key]];
            } else {
              // errors.audio_format = ['選択項目の中から選択してください。'];
            }
            break;
          case 'audio_channel':
            movie.main_video.videos[0].audio_channel = data.audio_channel;
            break;
          case 'file_format':
            if (film_format_name[data[key]]) {
              movie.main_video.videos[0].video_encoding_format = film_format_name[data[key]];
            } else {
              // errors.video_encoding_format = ['選択項目の中から選択してください。'];
            }
            break;
          default:
            break;
        }
      } else if (!isProviderImpersonate && key == 'subtitle_languages') {
        // errors.subtitle_languages = ['subtitle_languagesは必須項目です。'];
      }
    });

    // awards
    const award_keys = ['awards', 'awards_en', 'awards_year'];
    for (let i = 1; i < 6; i++) {
      const found = award_keys.find((key) => {
        return data[`${key}${i}`] === undefined || data[`${key}${i}`] === null || data[`${key}${i}`] === '';
      });
      if (found === undefined) {
        //all items filled, register
        const newAward: any = {
          translations: {
            ja: {
              name: data[`awards${i}`],
              translation_status: 1,
            },
            en: {
              name: data[`awards_en${i}`],
              translation_status: 1,
            },
          },
        };
        let registeredAward = AwardUtility.getRegisteredAward(newAward);
        if (registeredAward && !AwardUtility.isAwardExists(registeredAward, movie.awards)) {
        } else {
          const awardResult = await axios.post('/award', newAward);
          registeredAward = awardResult.data.data;
        }
        registeredAward['awarded_at'] = format(new Date(`${data[`awards_year${i}`]}-01-01`), 'yyyy-MM-dd');
        movie.awards.push(registeredAward);
      }
    }
    // play history
    const play_history_keys = ['play_history', 'play_history_en', 'play_history_startdate', 'play_history_enddate'];
    for (let i = 1; i < 4; i++) {
      const found = play_history_keys.find((key) => {
        return data[`${key}${i}`] === undefined || data[`${key}${i}`] === null || data[`${key}${i}`] === '';
      });
      if (found === undefined) {
        //data is set. register.
        movie.play_histories.push({
          start_at: format(data[`play_history_startdate${i}`], 'yyyy-MM-dd 00:00:00'),
          end_at: format(data[`play_history_enddate${i}`], 'yyyy-MM-dd 00:00:00'),
          translations: {
            ja: {
              name: data[`play_history${i}`],
              translation_status: 1,
            },
            en: {
              name: data[`play_history_en${i}`],
              translation_status: 1,
            },
          },
        });
      }
    }

    // svod history
    const vod_history_keys = ['vod_history', 'vod_history_en', 'vod_history_startdate', 'vod_history_enddate'];
    for (let i = 1; i < 4; i++) {
      const found = vod_history_keys.find((key) => {
        return data[`${key}${i}`] === undefined || data[`${key}${i}`] === null || data[`${key}${i}`] === '';
      });
      if (found === undefined) {
        //data is set. register.
        movie.svod_histories.push({
          start_at: format(data[`vod_history_startdate${i}`], 'yyyy-MM-dd 00:00:00'),
          end_at: format(data[`vod_history_enddate${i}`], 'yyyy-MM-dd 00:00:00'),
          translations: {
            ja: {
              name: data[`vod_history${i}`],
              translation_status: 1,
            },
            en: {
              name: data[`vod_history_en${i}`],
              translation_status: 1,
            },
          },
        });
      }
    }

    // // アワードのフォーマット設定
    // if (data['awards'] || data['awards_en']) {
    //   const awards = data['awards'].split(';');
    //   const awardEnglish = data['awards_en'].split(';');
    //   let award: any = {};
    //   if (awards.length == awardEnglish.length) {
    //     awards.forEach((awardName, key) => {
    //       award = {
    //         name: awardName,
    //         translations: {
    //           ja: {
    //             name: awardName,
    //             translation_status: 1,
    //           },
    //           en: {
    //             name: awardEnglish[key],
    //             translation_status: 1,
    //           },
    //         },
    //         awarded_at: '',
    //       };
    //       movie.awards.push(award);
    //     });
    //   } else {
    //     errors.awards = '登録するawardの数が異なります。';
    //   }
    // }

    // タグの登録処理
    if (isProviderImpersonate && data.tag) {
      const tagItem = {};
      await store.getters['getAllTags'].map((item) => {
        tagItem[item.translations.ja.name] = item.uuid;
        return tagItem;
      });
      const tags = data.tag.split(';');
      let tag: any = {};
      if (tags.length > 0) {
        tags.forEach((tagName) => {
          if (Object.keys(tagItem).indexOf(tagName) >= 0) {
            tag = {
              name: tagName,
              classes: 'tag-label-class',
              uuid: tagItem[tagName],
            };
            movie.tags.push(tag);
          }
        });
      }
    }

    // // 上映歴の登録
    // if (data['play_history']) {
    //   const histories = data['play_history'].split(';');
    //   let inputHistory: any = {};
    //   let historyDate = '';
    //   let historyName = '';
    //   let historyParts = [];
    //   if (histories.length > 0) {
    //     histories.forEach((history) => {
    //       historyParts = history.split('/');
    //       if (historyParts.length > 1) {
    //         historyName = historyParts[1];
    //         historyDate = historyParts[0].match(/[\d]+/g);
    //         historyDate = historyDate[0] + '-' + historyDate[1] + '-01';
    //         inputHistory = {
    //           start_at: historyDate,
    //           end_at: historyDate,
    //           translations: {
    //             ja: {
    //               name: historyName,
    //             },
    //             en: {
    //               name: historyName,
    //             },
    //           },
    //         };
    //         movie.play_histories.push(inputHistory);
    //       }
    //     });
    //   }
    // }

    // if (data['svod_history']) {
    //   const histories = data['svod_history'].split(';');
    //   let inputHistory: any = {};
    //   let historyDate = '';
    //   let historyName = '';
    //   let historyParts = [];
    //   if (histories.length > 0) {
    //     histories.forEach((history) => {
    //       historyParts = history.split('/');
    //       if (historyParts.length > 1) {
    //         historyName = historyParts[1];
    //         historyDate = historyParts[0].match(/[\d]+/g);
    //         historyDate = historyDate[0] + '-' + historyDate[1] + '-01';
    //         inputHistory = {
    //           start_at: historyDate,
    //           end_at: historyDate,
    //           translations: {
    //             ja: {
    //               name: historyName,
    //             },
    //             en: {
    //               name: historyName,
    //             },
    //           },
    //         };
    //         movie.svod_histories.push(inputHistory);
    //       }
    //     });
    //   }
    // }

    // 運用枠の情報設定
    if (!isProviderImpersonate) {
      movie.conditions = '';
      // movie.author = '';
      movie.status = 2;
      movie.conditions = '';
      movie.quality_rank = null;
      movie.region = null;
      // (movie.translations as any).ja.sns_note = '';
      // (movie.translations as any).en.sns_note = '';
      // (movie.translations as any).ja.news_note = '';
      // (movie.translations as any).en.news_note = '';
      // (movie.translations as any).ja.other_note = '';
      // (movie.translations as any).en.other_note = '';
      movie.tags = [];
    } else {
      const admin_data_keys = [
        // 'date_created',
        // 'author',
        'selling_price',
        'status',
        'conditions',
        'quality_rank',
        'region',
        'sns_note',
        'news_note',
        'other_note',
      ];
      admin_data_keys.forEach((key: string): void => {
        if (key == 'status') {
          let status: number = 1;
          switch (data.status) {
            case 'draft':
              status = 1;
              break;
            case 'requested':
              status = 2;
              break;
            case 'approved':
              status = 3;
              break;
            case 'requested_delete':
              status = 4;
              break;
            case 'declined':
              status = 5;
              break;
            case 'publish':
              status = 3;
              movie.approved_by = { uuid: manager.manager.uuid };
              break;
            default:
              status = 2;
              break;
          }
          movie.status = status;
          // } else if (key == 'region') {
          //   const regionOptions = [
          //     '北アメリカ',
          //     'ヨーロッパ',
          //     'オセアニア',
          //     '南アメリカ',
          //     'アジア',
          //     'カリブ海',
          //     'アフリカ',
          //   ];
          //   if (regionOptions.indexOf(data.region) >= 0) {
          //     movie.region = data.region;
          //   } else {
          //     movie.region = '';
          //   }
          // } else if (['sns_note', 'news_note', 'other_note'].indexOf(key) >= 0) {
          //   (movie.translations as any).ja[key] = data[key];
          //   validate(data[data.key + '_english'], { english: true }, { name: key }).then((result) => {
          //     if (result.valid) {
          //       (movie.translations as any).en[key] = data[key + '_english'];
          //     } else {
          //       translationErrors.en[key] = result.errors;
          //     }
          //   });
          // } else if (key == 'selling_price') {
          //   if (data.selling_price) {
          //     validate(data.selling_price, { integer: true }, { name: 'price' }).then((result) => {
          //       if (result.valid) {
          //         movie.prices = movie.prices.map((item) => {
          //           item.price = data.selling_price;
          //           return item;
          //         });
          //       } else {
          //         translationErrors.en[key] = result.errors;
          //       }
          //     });
          //   }
          // } else if (key == 'quality_rank') {
          //   let quality_rank: number | null = null;
          //   switch (data.quality_rank) {
          //     case 'A':
          //     case 'a':
          //       quality_rank = 1;
          //       break;
          //     case 'B':
          //     case 'b':
          //       quality_rank = 2;
          //       break;
          //     case 'C':
          //     case 'c':
          //       quality_rank = 3;
          //       break;
          //     case 'D':
          //     case 'd':
          //       quality_rank = 4;
          //       break;
          //   }
          //   movie.quality_rank = quality_rank;
        } else if (data[key] && key in movie) {
          movie[key] = data[key];
        }
      });
      // movie.author = data['author'];
      // movie.status = data['status'];
      // movie.conditions = data['conditions'];
      // movie.selling_price = data['selling_price'];
      // movie.quality_rank = data['quality_rank'];
      // movie.region = data['region'];
    }
    return Promise.resolve(movie);
  }
}
